import { FC } from 'react';
import { Add, Close, Save } from '@mui/icons-material';
import {
  FormControlLabel,
  FormControl,
  FormLabel,
  FormHelperText,
  Grid,
  MenuItem,
  TextField,
  Box,
  ButtonGroup,
  Button,
  Divider,
  Radio,
  RadioGroup
} from '@mui/material';
import { useQuery } from 'react-query';
import { Form, Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { getJobStatuses, getJobTypes, putJobGeneralInfo } from '../../../../fetch';
import { IDropdownResponse, IJobGeneralInfo } from '../../../../models';
import { Loader, SMBSelect, StatesDropdown } from '../../../../components';

interface IGeneralInformation {
  generalInfo: IJobGeneralInfo;
  isLoadingGeneralInfo: boolean;
  setDirtyForm: (val: boolean) => void;
  refetchGeneralInfo: () => void;
}

const GeneralInfoSchema = Yup.object().shape({
  jobId: Yup.number(),
  jobCode: Yup.string(),
  neighborhoodId: Yup.number(),
  status: Yup.string(),
  jobType: Yup.string(),
  jobForeignId: Yup.number(),
  address: Yup.string(),
  city: Yup.string(),
  state: Yup.string().required('Required'),
  zip: Yup.string().max(5, 'Max 5 characters').nullable(),
  lotIsMarketable: Yup.string(),
  hasBeenPlatted: Yup.string()
});

export const GeneralInformation: FC<IGeneralInformation> = ({
  generalInfo,
  isLoadingGeneralInfo,
  setDirtyForm,
  refetchGeneralInfo
}) => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { jobId }: { jobId: string } = useParams();

  const { isLoading: isLoadingJobStatuses, data: jobStatuses } = useQuery<
    IDropdownResponse[],
    Error
  >(['getJobStatuses'], () => getJobStatuses());

  const { isLoading: isLoadingJobTypes, data: jobTypes } = useQuery<IDropdownResponse[], Error>(
    ['getJobTypes'],
    () => getJobTypes()
  );

  return (
    <>
      {isLoadingGeneralInfo && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingGeneralInfo && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            ...generalInfo,
            jobId: generalInfo?.jobId ?? '',
            jobCode: generalInfo?.jobCode ?? '',
            neighborhoodId: generalInfo?.neighborhoodId ?? '',
            status: generalInfo?.status ?? '',
            jobType: generalInfo?.jobType ?? '',
            jobForeignId: generalInfo?.jobForeignId ?? '',
            address: generalInfo?.address ?? '',
            city: generalInfo?.city ?? '',
            state: generalInfo?.state ?? '',
            zip: generalInfo?.zip ?? '',
            lotIsMarketable:
              generalInfo?.lotIsMarketable === true
                ? 'Yes'
                : generalInfo?.lotIsMarketable === false
                ? 'No'
                : '',
            hasBeenPlatted:
              generalInfo?.hasBeenPlatted === true
                ? 'Yes'
                : generalInfo?.hasBeenPlatted === false
                ? 'No'
                : ''
          }}
          validationSchema={GeneralInfoSchema}
          onSubmit={async (values, actions) => {
            try {
              const data: IJobGeneralInfo = {
                ...values,
                lotIsMarketable: values.lotIsMarketable === 'Yes' ? true : false,
                hasBeenPlatted: values.hasBeenPlatted === 'Yes' ? true : false
              };

              if (jobId !== 'add') {
                await putJobGeneralInfo(parseInt(jobId), data);
                refetchGeneralInfo();
                enqueueSnackbar('Job has been updated successfully!', {
                  variant: 'success'
                });
              }
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Something went wrong. Plan was not updated.', {
                variant: 'error'
              });
            }
          }}
        >
          {({
            isSubmitting,
            values,
            setFieldValue,
            errors,
            touched,
            handleSubmit,
            dirty,
            isValid,
            handleBlur
          }) => {
            // This is also what Formik does in their official documentation.
            // https://stackoverflow.com/questions/61031464/setstate-called-in-render-prop-is-causing-a-react-warning
            // Also see https://github.com/jaredpalmer/formik/issues/1218
            if (dirty) {
              setTimeout(() => setDirtyForm(true), 0);
            } else {
              setTimeout(() => setDirtyForm(false), 0);
            }

            return (
              <>
                {isSubmitting && (
                  <Loader type="fullscreen" position="centered">
                    Saving...
                  </Loader>
                )}

                <Form onSubmit={handleSubmit}>
                  <Box mt={1}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item xs={12} sm={2}>
                            <FormControl component="fieldset">
                              <FormLabel>Has been Platted</FormLabel>
                              <RadioGroup
                                sx={{
                                  position: 'flex',
                                  flexDirection: 'row',
                                  justifyContent: 'left',
                                  alignItems: 'start'
                                }}
                                aria-label="hasBeenPlatted"
                                name="hasBeenPlatted"
                                value={values.hasBeenPlatted}
                                onChange={e => setFieldValue('hasBeenPlatted', e.target.value)}
                              >
                                <FormControlLabel value={'Yes'} control={<Radio />} label={'Yes'} />
                                <FormControlLabel value={'No'} control={<Radio />} label={'No'} />
                              </RadioGroup>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} sm={2}>
                            <FormControl component="fieldset">
                              <FormLabel>Lot is Marketable</FormLabel>
                              <RadioGroup
                                sx={{
                                  position: 'flex',
                                  flexDirection: 'row',
                                  justifyContent: 'left',
                                  alignItems: 'start'
                                }}
                                aria-label="lotIsMarketable"
                                name="lotIsMarketable"
                                value={values.lotIsMarketable}
                                onChange={e => setFieldValue('lotIsMarketable', e.target.value)}
                              >
                                <FormControlLabel value={'Yes'} control={<Radio />} label={'Yes'} />
                                <FormControlLabel value={'No'} control={<Radio />} label={'No'} />
                              </RadioGroup>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item xs={12}>
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              required
                              name="status"
                              label="Job Status"
                              id="status"
                              variant="outlined"
                              disabled={isLoadingJobStatuses}
                              value={values.status}
                              onChange={({ target: { value } }) => {
                                setFieldValue('status', value);
                              }}
                              onBlur={handleBlur}
                              error={errors && errors.status ? true : false}
                            >
                              {jobStatuses &&
                                jobStatuses.map((job, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={job.value}>
                                      {job.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                            {touched && touched.status && errors && errors.status && (
                              <FormHelperText error>Required</FormHelperText>
                            )}
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              required
                              name="jobType"
                              label="Job Type"
                              variant="outlined"
                              id="jobType"
                              disabled={isLoadingJobTypes}
                              value={values.jobType}
                              onChange={({ target: { value } }) => {
                                setFieldValue('jobType', value);
                              }}
                              onBlur={handleBlur}
                              error={errors && errors.jobType ? true : false}
                            >
                              {jobTypes &&
                                jobTypes.map((job, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={job.value}>
                                      {job.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                            {touched && touched.jobType && errors && errors.jobType && (
                              <FormHelperText error>Required</FormHelperText>
                            )}
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Address"
                              name="address"
                              size="small"
                              value={values.address}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('address', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="City"
                              name="city"
                              size="small"
                              value={values.city}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('city', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <StatesDropdown
                              isRequired
                              message={touched?.state && errors?.state ? 'Required' : undefined}
                              hasError={touched?.state && errors?.state ? true : undefined}
                              value={values.state}
                              handleChange={value => {
                                setFieldValue('state', value as string);
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Zip"
                              name="zip"
                              size="small"
                              value={values.zip}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('zip', e.target.value)}
                              error={Boolean(touched.zip && errors.zip)}
                              helperText={touched.zip && errors.zip}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Sage100 ID"
                              name="jobForeignId"
                              size="small"
                              value={values.jobForeignId}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('jobForeignId', e.target.value)}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box mt={1} mb={0.5}>
                    <Divider />
                  </Box>
                  <ButtonGroup>
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={jobId ? <Save /> : <Add />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        history.push('/development/jobs');
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Form>
              </>
            );
          }}
        </Formik>
      )}
    </>
  );
};
